import axios from 'axios';
import debounce from 'lodash.debounce';

/**
 * Creates a suggestion container element positioned relative to the input
 * @param {HTMLInputElement} inputEl - The input element to attach suggestions to
 * @returns {HTMLElement} The suggestion container element
 */
function createSuggestionContainer(inputEl) {
  const container = document.createElement('div');
  container.className = 'postgrid-autocomplete-container';
  container.role = 'listbox';
  
  // Insert after the input's parent container
  const inputContainer = inputEl.closest('.mt-1') || inputEl.parentElement;
  inputContainer.style.position = 'relative';
  inputContainer.appendChild(container);
  
  return container;
}

/**
 * Displays suggestions in the container
 * @param {HTMLElement} container - The suggestion container
 * @param {Array} suggestions - Array of suggestion strings
 * @param {string} typedAddress - The address the user typed
 * @param {Function} onSuggestionSelected - Callback when a suggestion is selected
 */
function displaySuggestions(container, suggestions, typedAddress, onSuggestionSelected) {
  // Clear existing suggestions
  container.innerHTML = '';
  
  if (!suggestions || suggestions.length === 0) {
    hideSuggestions(container);
    return;
  }
  
  // Add user's typed address as first suggestion
  const manualSuggestionEl = document.createElement('div');
  manualSuggestionEl.className = 'postgrid-suggestion-item postgrid-manual-entry';
  const spanEl = document.createElement('span');
  spanEl.className = 'manual-entry-text';
  spanEl.textContent = `Use "${typedAddress}"`;
  manualSuggestionEl.appendChild(spanEl);
  manualSuggestionEl.role = 'option';
  
  manualSuggestionEl.addEventListener('click', () => {
    onSuggestionSelected(typedAddress);
  });
  
  container.appendChild(manualSuggestionEl);
  
  // Add API suggestions
  suggestions.forEach((suggestion, index) => {
    const suggestionEl = document.createElement('div');
    suggestionEl.className = 'postgrid-suggestion-item';
    suggestionEl.textContent = suggestion;
    suggestionEl.role = 'option';
    
    suggestionEl.addEventListener('click', () => {
      onSuggestionSelected(suggestion);
    });
    
    container.appendChild(suggestionEl);
  });
  
  container.style.display = 'block';
}

/**
 * Hides the suggestion container
 * @param {HTMLElement} container - The suggestion container
 */
function hideSuggestions(container) {
  container.style.display = 'none';
}


/**
 * Fetches PostGrid autocomplete suggestions
 * @param {string} partialAddress - Partial address string
 * @returns {Promise<Array>} Array of suggestion strings
 */
async function getPostGridSuggestions(partialAddress) {
  const apiKey = import.meta.env.VITE_POSTGRID_API_KEY;
  
  if (!apiKey) {
    console.error('PostGrid API key not found in environment variables');
    return [];
  }
  
  if (!partialAddress || partialAddress.length < 5) {
    return [];
  }
  
  try {
    const response = await axios.get('https://api.postgrid.com/v1/intl_addver/completions', {
      params: {
        partialStreet: partialAddress,
        language: 'EN',
        countriesFilter: 'US',
        limit: 5
      },
      headers: {
        'x-api-key': apiKey
      }
    });
    
    if (!response.data || !response.data.data) {
      return [];
    }
    
    const suggestions = [];
    const containers = response.data.data.filter(item => item.type !== 'Address');
    const addresses = response.data.data.filter(item => item.type === 'Address');
    
    // Add direct addresses
    suggestions.push(...addresses.map(item => item.text));
    
    // Get detailed suggestions for container buildings (apartments, condos, etc.)
    if (containers.length > 0) {
      const containerPromises = containers.map(async (container) => {
        try {
          const containerResponse = await axios.get('https://api.postgrid.com/v1/intl_addver/completions', {
            params: {
              container: container.id,
              language: 'EN',
              advanced: 'true',
            },
            headers: {
              'x-api-key': apiKey
            }
          });
          
          return containerResponse.data.data.map(item => item.text);
        } catch (error) {
          console.warn('Failed to fetch container details:', error);
          return [];
        }
      });
      
      const containerResults = await Promise.all(containerPromises);
      suggestions.push(...containerResults.flat());
    }
    
    return suggestions;
  } catch (error) {
    console.error('PostGrid autocomplete error:', error);
    return [];
  }
}

/**
 * Verifies and parses a PostGrid suggestion into a structured address object
 * @param {string} suggestion - Selected suggestion string
 * @returns {Promise<Object>} Parsed address object
 */
async function parsePostGridSuggestion(suggestion) {
  const apiKey = import.meta.env.VITE_POSTGRID_API_KEY;
  
  if (!apiKey) {
    console.error('PostGrid API key not found in environment variables');
    // Fallback to basic parsing
    return await parseBasicAddress(suggestion);
  }
  
  try {
    const response = await axios.post('https://api.postgrid.com/v1/addver/verifications', {
      address: suggestion
    }, {
      params: {
        includeDetails: true
      },
      headers: {
        'x-api-key': apiKey
      }
    });

    if (!response.data || !response.data.data) {
      throw new Error('Invalid response from PostGrid verification API');
    }

    const verifiedAddress = response.data.data;
    
    return {
      addressLine1: verifiedAddress.line1 || '',
      addressLine2: verifiedAddress.line2 || '',
      city: verifiedAddress.city || '',
      state: verifiedAddress.provinceOrState || '',
      county: verifiedAddress.details?.county || '',
      zipcode: verifiedAddress.postalOrZip || '',
    };
  } catch (error) {
    console.error('PostGrid address verification error:', error);
    
    // Fallback to basic parsing if verification fails
    return await parseBasicAddress(suggestion);
  }
}

/**
 * Basic address parsing fallback
 * @param {string} suggestion - Address suggestion string
 * @returns {Object} Basic parsed address object
 */
async function parseBasicAddress(suggestion) {
  try {
    const apiKey = import.meta.env.VITE_POSTGRID_API_KEY;

    if (!apiKey) {
      console.error('PostGrid API key not found in environment variables');
      // Fallback to basic parsing
      throw new Error('No API key found in environment variables');
    }

    const response = await axios.post('https://api.postgrid.com/v1/addver/parses', {
      address: suggestion
    }, {
      params: {
        includeDetails: true
      },
      headers: {
        'x-api-key': apiKey
      }
    });

    if (!response.data || !response.data.data) {
      throw new Error('Invalid response from PostGrid parse API');
    }

    const parsedAddress = response.data.data;

    const parts = [
      parsedAddress.houseNumber,
      parsedAddress.road,
      parsedAddress.unit,
      parsedAddress.poBox
    ].filter(part => part && part.trim());

    return {
      addressLine1: `${parts.join(' ')}` || '',
      addressLine2: '',
      city: parsedAddress.city || '',
      state: parsedAddress.state || '',
      county: '',
      zipcode: parsedAddress.postcode || '',
    };
  } catch (error) {
    console.error('PostGrid address verification error:', error);

    // Fallback to basic parsing if API calls fail
    const parts = suggestion.split(' ');
    const zipMatch = parts[parts.length - 1].match(/\d{5}/);
    const stateMatch = parts[parts.length - 2];

    return {
      addressLine1: parts.slice(0, -2).join(' '),
      addressLine2: '',
      city: parts[parts.length - 3] || '',
      state: stateMatch || '',
      county: '',
      zipcode: zipMatch ? zipMatch[0] : '',
    };
  }
}

/**
 * Sets up PostGrid autocomplete on an input element
 * @param {HTMLInputElement} inputEl - The input element to attach autocomplete to
 * @param {Function} onAddressParsed - Callback function that receives the parsed address object
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {Function} options.onSuggestionsReceived - Optional callback when suggestions are received
 * @returns {Object} Object containing the input element and suggestion container
 */
export function setupPostGridAutocomplete(inputEl, onAddressParsed, options = {}) {
  const {
    debounceMs = 500,
    onSuggestionsReceived = null
  } = options;
  
  if (!inputEl) {
    console.warn('PostGrid autocomplete: input element not provided');
    return null;
  }
  
  // Create suggestion container
  const suggestionContainer = createSuggestionContainer(inputEl);
  
  // Debounced input handler
  const handleInput = debounce(async (event) => {
    const inputValue = event.target.value.trim();
    
    if (!inputValue.length) {
      hideSuggestions(suggestionContainer);
      return;
    }
    
    try {
      const suggestions = await getPostGridSuggestions(inputValue);
      
      if (onSuggestionsReceived) {
        onSuggestionsReceived(suggestions);
      }
      
      displaySuggestions(suggestionContainer, suggestions, inputValue, async (selectedSuggestion) => {
        try {
          // Update input value
          inputEl.value = selectedSuggestion;

          // Parse and return address object
          // Use basic parsing for manual entry (first suggestion), PostGrid parsing for API suggestions
          const parsedAddress = selectedSuggestion === inputValue 
            ? await parseBasicAddress(selectedSuggestion)
            : await parsePostGridSuggestion(selectedSuggestion);
          onAddressParsed(parsedAddress);

          // Hide suggestions
          hideSuggestions(suggestionContainer);
        } catch (error) {
          console.error('Error processing selected suggestion:', error);
          // Still hide suggestions even if parsing fails
          hideSuggestions(suggestionContainer);
        }
      });
    } catch (error) {
      console.error('PostGrid autocomplete error:', error);
      hideSuggestions(suggestionContainer);
    }
  }, debounceMs);
  
  // Attach event listeners
  inputEl.addEventListener('input', handleInput);
  
  // Hide suggestions when clicking outside
  document.addEventListener('click', (event) => {
    if (!inputEl.contains(event.target) && !suggestionContainer.contains(event.target)) {
      hideSuggestions(suggestionContainer);
    }
  });
  
  // Handle keyboard navigation
  inputEl.addEventListener('keydown', (event) => {
    const suggestions = suggestionContainer.querySelectorAll('.postgrid-suggestion-item');
    
    if (suggestions.length === 0) return;
    
    // Get current active index
    let activeIndex = -1;
    suggestions.forEach((el, index) => {
      if (el.classList.contains('active')) {
        activeIndex = index;
      }
    });
    
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      
      // Clear all active states
      suggestions.forEach(el => {
        el.classList.remove('active');
      });
      
      // Set new active index
      const newIndex = activeIndex < suggestions.length - 1 ? activeIndex + 1 : 0;
      const newActiveEl = suggestions[newIndex];
      
      newActiveEl.classList.add('active');
      
      // Scroll into view
      newActiveEl.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
      
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      
      // Clear all active states
      suggestions.forEach(el => {
        el.classList.remove('active');
      });
      
      // Set new active index
      const newIndex = activeIndex > 0 ? activeIndex - 1 : suggestions.length - 1;
      const newActiveEl = suggestions[newIndex];
      
      newActiveEl.classList.add('active');
      
      // Scroll into view
      newActiveEl.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
      
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const activeEl = suggestionContainer.querySelector('.postgrid-suggestion-item.active');
      if (activeEl) {
        activeEl.click();
      } else {
        // If no suggestion is active, accept the typed address
        const typedAddress = inputEl.value.trim();
        if (typedAddress) {
          // Find the manual entry suggestion and click it
          const manualEntry = suggestionContainer.querySelector('.postgrid-manual-entry');
          if (manualEntry) {
            manualEntry.click();
          }
        }
      }
    } else if (event.key === 'Escape') {
      // Clear all states when hiding
      suggestions.forEach(el => {
        el.classList.remove('active');
      });
      hideSuggestions(suggestionContainer);
    }
  });
  
  return {
    inputEl,
    suggestionContainer
  };
}

/**
 * Sets up multiple PostGrid autocomplete instances for primary and secondary addresses
 * @param {Object} refs - Vue refs object containing searchPrimary and searchSecondary
 * @param {Function} onPrimaryAddressParsed - Callback for primary address
 * @param {Function} onSecondaryAddressParsed - Callback for secondary address
 * @param {Object} options - Configuration options (same as setupPostGridAutocomplete)
 * @returns {Object} Object containing the autocomplete instances
 */
export function setupDualPostGridAutocomplete(refs, onPrimaryAddressParsed, onSecondaryAddressParsed, options = {}) {
  const instances = {};
  
  if (refs.searchPrimary) {
    instances.primary = setupPostGridAutocomplete(refs.searchPrimary, onPrimaryAddressParsed, options);
  }
  
  if (refs.searchSecondary) {
    instances.secondary = setupPostGridAutocomplete(refs.searchSecondary, onSecondaryAddressParsed, options);
  }
  
  return instances;
}
